import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import InfoMessage from '@eika/attention-message/lib/InfoMessage';
import { ButtonGroup, LinkButton, StandardButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { SuccessLabel } from '@eika/label';
import { Loader } from '@eika/loader';

import { useClick2PayProfileEmailUpdateMutation } from '@/react-query/hooks/mutation/use-click2pay';
import { useClick2PayProfile } from '@/react-query/hooks/query/use-click2pay';
import { useCurrentCustomer } from '@/react-query/hooks/query/use-customer';

import { CouldNotFetchCustomerInfoErrorCard } from '@components/error/ErrorCard';
import { Click2PayProcessStatus } from '@generated-types';
import useText from '@hooks/use-text';
import { getClick2PayUrl } from '@pages/click2pay/functions';
import click2PayRoutes from '@pages/click2pay/routes';
import { formatPhoneNumber } from '@utils/text-format-utils';

import './Click2PayInfoReview.scss';

const Click2PayInfoReview = () => {
    const { data: customer, isPending, isError } = useCurrentCustomer();
    const updateEmailMutation = useClick2PayProfileEmailUpdateMutation();
    const text = useText();
    const { data: profile } = useClick2PayProfile();

    const [queryParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const isAdmin = location.pathname.includes('admin');

    const currentEmail = profile?.customerProfile?.email || customer?.contact.email;
    const [newEmail, setNewEmail] = useState(currentEmail ?? '');
    const [emailError, setEmailError] = useState<string | null>(null);
    const [emailPending, setIsPending] = useState(false);
    const [emailSuccess, setEmailSuccess] = useState(false);
    const { chooseCard } = click2PayRoutes;

    useEffect(() => {
        if (currentEmail && !newEmail) {
            setNewEmail(currentEmail);
        }
    }, [currentEmail, newEmail]);

    const validateEmail = (value: string): string | null => {
        const result = z
            .string()
            .trim()
            .min(1, { message: text.userInfoErrorEmailEmpty })
            .email({ message: text.userInfoErrorEmailFormat })
            .safeParse(value);

        return result.success ? null : result.error.errors[0]?.message || text.userInfoErrorEmailFormat;
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmailSuccess(false);
        setNewEmail(value);
        setEmailError(validateEmail(value));
    };

    const next = () => {
        queryParams.set('email', newEmail);
        navigate(getClick2PayUrl(queryParams, chooseCard));
    };

    const updateEmail = async () => {
        setIsPending(true);
        const response = await updateEmailMutation.mutateAsync(newEmail);
        if (response.status === Click2PayProcessStatus.SUCCESS) {
            setIsPending(false);
            setEmailSuccess(true);
        }
    };

    if (isPending) return <Loader />;
    if (isError) return <CouldNotFetchCustomerInfoErrorCard />;

    return (
        <div className="click2pay-info-review">
            <InfoCard className="click2pay-contact-info">
                <section>
                    <h4>Navn</h4>
                    <p>
                        {customer?.firstName} {customer?.lastName}
                    </p>
                </section>
                <section>
                    <h4>Adresse</h4>
                    <p>
                        {customer?.address.address}, {customer?.address.postalCode} {customer?.address.city}
                    </p>
                    <p>{customer?.address.country}</p>
                </section>
                <section>
                    <h4>Telefon</h4>
                    <p>{formatPhoneNumber(customer?.contact.mobile ?? '')}</p>
                </section>
                <InfoMessage
                    className="click2pay-info-message"
                    text="For å endre telefonnummeret må du oppdatere det i kontaktinfo-innstillingene i mobilbanken"
                    type="default"
                />
                <section className="email-section">
                    <div className="label-with-action">
                        <span className="label-text">E-post</span>
                    </div>
                    <Input
                        value={newEmail || currentEmail}
                        onChange={handleEmailChange}
                        placeholder="Skriv inn e-post"
                        error={emailError || ''}
                    />

                    <div className="update-button-wrapper">
                        {emailPending && <Loader className="email-loader" mini />}
                        {emailSuccess && (
                            <SuccessLabel icon={IconTypes.USP_CHECKMARK_24} text={text.click2PayEmailUpdated} />
                        )}
                        {!emailPending && !emailSuccess && isAdmin && newEmail !== currentEmail && (
                            <StandardButton
                                onClick={updateEmail}
                                disabled={!!emailError}
                                className="update-email-button"
                            >
                                {text.click2PayUpdate}
                            </StandardButton>
                        )}
                    </div>
                </section>
                <InfoMessage
                    className="click2pay-info-message"
                    text="Dette er e-posten du bruker for å få tilgang til kortene dine"
                    type="default"
                />
            </InfoCard>
            {isAdmin ? (
                <ButtonGroup
                    primaryAction={
                        <StandardButton onClick={() => navigate('/')}>{text.click2PayGoToHome}</StandardButton>
                    }
                />
            ) : (
                <ButtonGroup
                    primaryAction={
                        <StandardButton onClick={next} disabled={!!emailError || !newEmail.trim()}>
                            {text.click2PayNext}
                        </StandardButton>
                    }
                    tertiaryAction={<LinkButton onClick={() => navigate('/')}>{text.click2PayCancel}</LinkButton>}
                />
            )}
        </div>
    );
};

export default Click2PayInfoReview;
